import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { ClientThemeConfig } from './client-theme.model';

/**
 * Aplica configuração de whitelabel sobrescrevendo os tokens
 * --rsm-* definidos em theme/_rsm-tokens.scss.
 *
 * Uso típico:
 *   themeService.applyTheme({
 *     primaryColor: '#208BFF',
 *     primaryDark:  '#0F5DB2',
 *     accentColor:  '#7E29FF',
 *     logoUrl:      '/assets/clients/acme/logo.svg',
 *   });
 *
 * Componentes que precisam reagir à mudança de logo (sidebar, login)
 * devem se inscrever em `logoUrl$`.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly logoUrlSubject = new BehaviorSubject<string | null>(null);
  private readonly clientNameSubject = new BehaviorSubject<string | null>(null);

  readonly logoUrl$: Observable<string | null> = this.logoUrlSubject.asObservable();
  readonly clientName$: Observable<string | null> = this.clientNameSubject.asObservable();

  applyTheme(config: ClientThemeConfig): void {
    const root = document.documentElement;
    this.setVar(root, '--rsm-primary',          config.primaryColor);
    this.setVar(root, '--rsm-primary-dark',     config.primaryDark);
    this.setVar(root, '--rsm-primary-hover-bg', config.primaryHoverBg);
    this.setVar(root, '--rsm-primary-light',    config.primaryLight);
    this.setVar(root, '--rsm-secondary',        config.accentColor);
    this.setVar(root, '--rsm-sidebar-bg',       config.sidebarBg);

    this.logoUrlSubject.next(config.logoUrl ?? null);
    this.clientNameSubject.next(config.clientName ?? null);
  }

  /** Reverte ao tema RSM padrão (apaga overrides; tokens do CSS voltam a vigorar). */
  resetTheme(): void {
    const root = document.documentElement;
    [
      '--rsm-primary',
      '--rsm-primary-dark',
      '--rsm-primary-hover-bg',
      '--rsm-primary-light',
      '--rsm-secondary',
      '--rsm-sidebar-bg',
    ].forEach((prop) => root.style.removeProperty(prop));
    this.logoUrlSubject.next(null);
    this.clientNameSubject.next(null);
  }

  private setVar(root: HTMLElement, prop: string, value: string | undefined): void {
    if (value) {
      root.style.setProperty(prop, value);
    }
  }
}
